import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Store as StoreIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as RevenueIcon,
  PointOfSale as SalesIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';

const Dashboard = ({ branches: propBranches, setHasBranches: propSetHasBranches }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [hasBranches, setHasBranches] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Show access denied notification when redirected from a protected route
  useEffect(() => {
    if (location.state?.accessDenied) {
      showNotification('You do not have permission to access that page.', 'warning');
      // Clear the state so it doesn't show again on refresh (without triggering re-render)
      window.history.replaceState({}, '');
    }
  }, [location.state, showNotification]);

  // Redirect super admins to their dashboard
  useEffect(() => {
    if (user?.isSuperAdmin) {
      navigate('/super-admin');
      return;
    }
  }, [user, navigate]);

  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      // Try business owner dashboard first, fall back to profile dashboard
      let response;
      try {
        response = await api.get('/business-owner/dashboard');
      } catch (err) {
        if (err.response?.status === 403) {
          // If business owner access denied, try profile dashboard
          response = await api.get('/profile/dashboard');
        } else {
          throw err;
        }
      }

      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Error loading dashboard data');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propBranches) {
      setHasBranches(propBranches.length > 0);
      if (propBranches.length > 0) {
        fetchDashboardData();
      } else {
        setLoading(false);
      }
      return;
    }
    let isMounted = true;
    const checkBranchesAndFetchData = async () => {
      try {
        // First check if branches exist
        const branchResponse = await api.get('/branches');
        if (isMounted) {
          const hasBranchesCheck = branchResponse.data.branches.length > 0;
          setHasBranches(hasBranchesCheck);

          if (hasBranchesCheck) {
            // If branches exist, fetch dashboard data
            await fetchDashboardData();
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error checking branches:', error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    checkBranchesAndFetchData();
    return () => {
      isMounted = false;
    };
  }, [propBranches]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading dashboard data. Please try again later.
        </Alert>
      </Box>
    );
  }

  if (!hasBranches && user?.role === 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          Welcome back, {user?.firstName}!
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 4,
            border: 1,
            borderColor: theme.palette.divider,
            borderRadius: 2,
            backgroundColor: theme.palette.background.default,
            textAlign: 'center'
          }}
        >
          <StoreIcon
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              mb: 2
            }}
          />
          <Typography variant="h5" gutterBottom>
            Let's Set Up Your First Branch
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
          >
            To get started with your POS system, you'll need to create at least one branch.
            This will be your main branch where you can manage your inventory, sales, and staff.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/branches', { state: { openAddDialog: true } })}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Create Your First Branch
          </Button>
        </Paper>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Today's Revenue",
      value: `KES ${(dashboardData?.todayStats?.revenue || 0).toLocaleString()}`,
      growth: dashboardData?.growth?.revenueGrowth || 0,
      growthLabel: 'vs last week',
      icon: <RevenueIcon />,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.08),
    },
    {
      title: "Today's Sales",
      value: dashboardData?.todayStats?.sales || 0,
      growth: dashboardData?.growth?.salesGrowth || 0,
      growthLabel: 'vs last week',
      icon: <SalesIcon />,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.08),
    },
    {
      title: 'Active Branches',
      value: dashboardData?.todayStats?.activeBranches || 0,
      subtitle: 'Branches with sales today',
      icon: <StoreIcon />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.08),
    },
    {
      title: 'Stock Alerts',
      value: (dashboardData?.alerts?.outOfStock || 0) + (dashboardData?.alerts?.lowStock || 0),
      subtitle: `${dashboardData?.alerts?.outOfStock || 0} out of stock, ${dashboardData?.alerts?.lowStock || 0} low stock`,
      icon: <WarningIcon />,
      color: (dashboardData?.alerts?.outOfStock || 0) > 0 ? theme.palette.error.main : theme.palette.warning.main,
      bgColor: (dashboardData?.alerts?.outOfStock || 0) > 0
        ? alpha(theme.palette.error.main, 0.08)
        : alpha(theme.palette.warning.main, 0.08),
    },
  ];

  const quickActions = [
    {
      title: 'Quick Sale',
      description: 'Record a new sale',
      icon: <AddIcon />,
      path: '/app/sales',
      color: theme.palette.primary.main,
      roles: ['admin', 'branch_admin', 'cashier'],
    },
    {
      title: 'Inventory',
      description: 'Check stock levels',
      icon: <InventoryIcon />,
      path: '/app/inventory',
      color: theme.palette.info.main,
      roles: ['admin'],
    },
    {
      title: 'Branches',
      description: 'Branch performance',
      icon: <StoreIcon />,
      path: '/app/branches',
      color: theme.palette.success.main,
      roles: ['admin'],
    },
    {
      title: 'Team',
      description: 'Manage staff',
      icon: <PeopleIcon />,
      path: '/app/users',
      color: theme.palette.secondary.main,
      roles: ['admin'],
    },
  ].filter(action => action.roles.includes(user?.role));

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      bgcolor: 'background.default',
      minHeight: 'calc(100vh - 128px)'
    }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}
          >
            Welcome back, {dashboardData?.userInfo?.name || user?.firstName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh dashboard">
          <IconButton
            onClick={fetchDashboardData}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
            }}
          >
            <RefreshIcon sx={{ color: 'primary.main' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(card.color, 0.15)}`,
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {card.title}
                  </Typography>
                  <Avatar
                    sx={{
                      width: 42,
                      height: 42,
                      bgcolor: card.bgColor,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: '1.75rem', color: 'text.primary' }}>
                  {card.value}
                </Typography>
                {card.growth !== undefined && card.growthLabel && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {card.growth >= 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: card.growth >= 0 ? 'success.main' : 'error.main',
                      }}
                    >
                      {card.growth >= 0 ? '+' : ''}{card.growth}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.growthLabel}
                    </Typography>
                  </Box>
                )}
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Branch Performance + Quick Actions Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Branch Performance */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card elevation={0} sx={{ height: '100%' }}>
            <Box sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Branch Performance
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/app/branches')}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ px: 3, pb: 3 }}>
              {(!dashboardData?.branches || dashboardData.branches.length === 0) ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <StoreIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No branch data available yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dashboardData.branches.map((branch) => (
                    <Box
                      key={branch.id}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            fontSize: '1rem',
                            fontWeight: 700,
                          }}
                        >
                          {branch.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {branch.name}
                            </Typography>
                            <Chip
                              label={branch.status === 'active' ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: branch.status === 'active'
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.error.main, 0.1),
                                color: branch.status === 'active' ? 'success.dark' : 'error.dark',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {branch.code} {branch.hasSalesToday ? '• Sales today' : '• No sales today'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Sales
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {branch.todaySales}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Revenue
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            KES {branch.todayRevenue?.toLocaleString() || '0'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%' }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Quick Actions
              </Typography>
            </Box>
            <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {quickActions.map((action, index) => (
                <Box
                  key={index}
                  onClick={() => navigate(action.path)}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      borderColor: alpha(action.color, 0.4),
                      bgcolor: alpha(action.color, 0.04),
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: alpha(action.color, 0.1),
                      color: action.color,
                    }}
                  >
                    {action.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {action.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Sales Activity */}
      <Card elevation={0}>
        <Box sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Sales Activity
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/app/sales')}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            View All
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!dashboardData?.recentSales || dashboardData.recentSales.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                    <OrdersIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No recent sales
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Sales transactions will appear here once you start making sales.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                dashboardData.recentSales.slice(0, 5).map((sale) => (
                  <TableRow
                    key={sale.id}
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {sale.saleNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{sale.customer || 'Walk-in Customer'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sale.branch}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(sale.time).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                        KES {sale.amount?.toLocaleString() || '0'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default Dashboard;
